import { createApi } from '@reduxjs/toolkit/query/react';

import { apiAppointmentServiceTypes, apiCalendarFrequencies, apiCarePlans, apiClients } from '~constants/api';

import { ApiError, Paginated, PaginatedRequest, rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';
import { get, post } from '~libs/genericAction';

import { budgetsApi } from '~support-at-home/actions/budgets';

import {
  AppointmentServiceTypeOption,
  CalendarFrequencyOption,
  CarePlan,
  CarePlanCulturalSafety,
  CarePlanGoal,
  CarePlanLookup,
  CarePlanNeed,
  CarePlanNotification,
  CarePlanReview,
  CarePlanServiceLine,
  CarePlanSummaryStatus,
  ClientCarePlanSummary,
} from '~clients/models';

// Care Plans RTK Query API. Endpoints map 1:1 to the tp-assist-api routes under
// /v2/clients/care-plans (see routes/client_care_plans.mjs) and mirror their verb
// coverage: needs/goals/service-lines are full CRUD; cultural safety is 1:1 (get
// single record, create, update — no list/delete); reviews are append-only (list +
// create); notifications are create-only (get-one, get-all, create); lookups are
// read-only. Ids are always in the path — no ?client_id/?care_plan_id query params.
//
// The slice's baseUrl is apiCarePlans (/v2/clients/care-plans). The per-client list +
// create live at /v2/clients/:id/care-plans, which is OFF that base, so they use a
// queryFn against apiClients to bypass the baseUrl rather than splitting the slice.
// Modelled on weekly-planner/actions/appointments (getOnHold).

export const typePrefix = `clients-care-plans`;

export type CarePlanListReq = { clientId: number };
export type CreateCarePlanReq = { clientId: number } & Partial<CarePlan>;
export type CarePlanChildListReq = { planId: string };
export type ClientsSummaryReq = PaginatedRequest & { status?: CarePlanSummaryStatus };

const toApiError = (e: any, context: string): ApiError => ({
  status: e?.status ?? 'FETCH_ERROR',
  message: e?.message ?? `Unknown error in ${context}`,
  data: e?.data,
});

// Add a freshly-created plan to a cached getCarePlans list unless it's already there.
// Exported for testing: covers the "plan not yet in the fetched list" branch the
// createCarePlan optimistic update depends on. Idempotent so re-applying (e.g. once the
// authoritative refetch has already inserted it) never duplicates.
export const upsertCarePlanIntoList = (draft: CarePlan[], plan: CarePlan): void => {
  if (!draft.some((existing) => existing.id === plan.id)) draft.push(plan);
};

export const carePlansApi = createApi({
  reducerPath: 'carePlansApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiCarePlans }),
  tagTypes: [
    'carePlan',
    'carePlanNeed',
    'carePlanGoal',
    'carePlanServiceLine',
    'carePlanCulturalSafety',
    'carePlanReview',
    'carePlanNotification',
  ],
  endpoints: (builder) => ({
    // ── Care plans ──────────────────────────────────────────────────────────
    // List + create are OFF the slice base (/v2/clients/:id/care-plans) — built off
    // apiClients via queryFn to bypass baseUrl.
    getCarePlans: builder.query<CarePlan[], CarePlanListReq>({
      async queryFn({ clientId }, _api) {
        try {
          const payload = await get(`${apiClients}/${clientId}/care-plans`, undefined, undefined, _api.signal);
          return { data: unwrap<CarePlan[]>(payload) };
        } catch (e) {
          return { error: toApiError(e, 'getCarePlans') };
        }
      },
      providesTags: [{ type: 'carePlan', id: 'LIST' }],
    }),

    createCarePlan: builder.mutation<CarePlan, CreateCarePlanReq>({
      async queryFn({ clientId, ...body }) {
        try {
          const payload = await post(`${apiClients}/${clientId}/care-plans`, body);
          return { data: unwrap<CarePlan>(payload) };
        } catch (e) {
          return { error: toApiError(e, 'createCarePlan') };
        }
      },
      // Optimistically insert the created plan into the cached list so the view's
      // auto-select (onCreated → selectPlan) resolves to it immediately. Without this,
      // selectPlan runs before the LIST invalidation refetch lands, so for a client with
      // existing plans the selection briefly falls back to sortedPlans[0] (the wrong plan)
      // until the refetch reconciles. The invalidatesTags refetch stays authoritative.
      async onQueryStarted({ clientId }, { dispatch, queryFulfilled }) {
        try {
          const { data: created } = await queryFulfilled;
          dispatch(
            carePlansApi.util.updateQueryData('getCarePlans', { clientId }, (draft) => {
              upsertCarePlanIntoList(draft, created);
            }),
          );
        } catch {
          // Create failed — nothing to insert; the error surfaces via the mutation state.
        }
      },
      invalidatesTags: [
        { type: 'carePlan', id: 'LIST' },
        { type: 'carePlan', id: 'SUMMARY' },
      ],
    }),

    getCarePlan: builder.query<CarePlan, string>({
      query: (id) => ({ url: `/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'carePlan', id }],
      transformResponse: (payload) => unwrap<CarePlan>(payload),
    }),

    // Care-plan client list view — computed status. Lives at the apiCarePlans root
    // (the per-client list moved off the root, freeing it for this endpoint).
    getClientsSummary: builder.query<Paginated<ClientCarePlanSummary>, ClientsSummaryReq | void>({
      query: (params) => ({ url: '', method: 'GET', params: params ?? undefined }),
      providesTags: [{ type: 'carePlan', id: 'SUMMARY' }],
      transformResponse: (payload) => unwrap<Paginated<ClientCarePlanSummary>>(payload),
    }),

    updateCarePlan: builder.mutation<void, { id: string } & Partial<CarePlan>>({
      query: ({ id, ...body }) => ({ url: `/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'carePlan', id: 'LIST' },
        { type: 'carePlan', id },
        { type: 'carePlan', id: 'SUMMARY' },
      ],
    }),

    // Activation is validated server-side; on failure the API returns 400 with a
    // descriptive message (see the 400-vs-422 note in tp-assist-api).
    activateCarePlan: builder.mutation<CarePlan, string>({
      query: (id) => ({ url: `/${id}/activate`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'carePlan', id: 'LIST' },
        { type: 'carePlan', id },
        { type: 'carePlan', id: 'SUMMARY' },
      ],
      transformResponse: (payload) => unwrap<CarePlan>(payload),
    }),

    // ── Budget linkage (care_plan_id FK on client_budgets) ───────────────────
    // Link/unlink flip the budget's care_plan_id. Both invalidate the budgetsApi
    // 'budgets' list (separate slice) and the plan's own tags, since activation
    // eligibility depends on the linked budgets.
    linkCarePlanBudget: builder.mutation<void, { planId: string; budgetId: string }>({
      query: ({ planId, budgetId }) => ({ url: `/${planId}/budgets`, method: 'POST', body: { budget_id: budgetId } }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(budgetsApi.util.invalidateTags([{ type: 'budgets', id: 'LIST' }]));
      },
      invalidatesTags: (_result, _error, { planId }) => [
        { type: 'carePlan', id: planId },
        { type: 'carePlan', id: 'SUMMARY' },
      ],
    }),

    unlinkCarePlanBudget: builder.mutation<void, { planId: string; budgetId: string }>({
      query: ({ planId, budgetId }) => ({ url: `/${planId}/budgets/${budgetId}`, method: 'DELETE' }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(budgetsApi.util.invalidateTags([{ type: 'budgets', id: 'LIST' }]));
      },
      invalidatesTags: (_result, _error, { planId }) => [
        { type: 'carePlan', id: planId },
        { type: 'carePlan', id: 'SUMMARY' },
      ],
    }),

    // ── Assessed needs (full CRUD) ──────────────────────────────────────────
    getCarePlanNeeds: builder.query<CarePlanNeed[], CarePlanChildListReq>({
      query: ({ planId }) => ({ url: `/${planId}/needs`, method: 'GET' }),
      providesTags: [{ type: 'carePlanNeed', id: 'LIST' }],
      transformResponse: (payload) => unwrap<CarePlanNeed[]>(payload),
    }),
    createCarePlanNeed: builder.mutation<CarePlanNeed, { planId: string } & Partial<CarePlanNeed>>({
      query: ({ planId, ...body }) => ({ url: `/${planId}/needs`, method: 'POST', body }),
      invalidatesTags: [{ type: 'carePlanNeed', id: 'LIST' }],
      transformResponse: (payload) => unwrap<CarePlanNeed>(payload),
    }),
    updateCarePlanNeed: builder.mutation<void, { id: string } & Partial<CarePlanNeed>>({
      query: ({ id, ...body }) => ({ url: `/needs/${id}`, method: 'PATCH', body }),
      invalidatesTags: [{ type: 'carePlanNeed', id: 'LIST' }],
    }),
    deleteCarePlanNeed: builder.mutation<void, string>({
      query: (id) => ({ url: `/needs/${id}`, method: 'DELETE' }),
      // goals.need_id is ON DELETE SET NULL, so deleting a need nulls its goals' need_id
      // server-side — invalidate the goals list too so a warm cache doesn't keep showing a
      // link to a now-deleted need.
      invalidatesTags: [
        { type: 'carePlanNeed', id: 'LIST' },
        { type: 'carePlanGoal', id: 'LIST' },
      ],
    }),
    // Bulk reorder — persists the assessed-needs display order (each need's sequence is
    // set to its index in `order` server-side). See PATCH .../needs/sort-order.
    reorderCarePlanNeeds: builder.mutation<void, { planId: string; order: string[] }>({
      query: ({ planId, order }) => ({ url: `/${planId}/needs/sort-order`, method: 'PATCH', body: { order } }),
      invalidatesTags: [{ type: 'carePlanNeed', id: 'LIST' }],
    }),

    // ── Goals (full CRUD) ───────────────────────────────────────────────────
    getCarePlanGoals: builder.query<CarePlanGoal[], CarePlanChildListReq>({
      query: ({ planId }) => ({ url: `/${planId}/goals`, method: 'GET' }),
      providesTags: [{ type: 'carePlanGoal', id: 'LIST' }],
      transformResponse: (payload) => unwrap<CarePlanGoal[]>(payload),
    }),
    createCarePlanGoal: builder.mutation<CarePlanGoal, { planId: string } & Partial<CarePlanGoal>>({
      query: ({ planId, ...body }) => ({ url: `/${planId}/goals`, method: 'POST', body }),
      invalidatesTags: [{ type: 'carePlanGoal', id: 'LIST' }],
      transformResponse: (payload) => unwrap<CarePlanGoal>(payload),
    }),
    updateCarePlanGoal: builder.mutation<void, { id: string } & Partial<CarePlanGoal>>({
      query: ({ id, ...body }) => ({ url: `/goals/${id}`, method: 'PATCH', body }),
      invalidatesTags: [{ type: 'carePlanGoal', id: 'LIST' }],
    }),
    deleteCarePlanGoal: builder.mutation<void, string>({
      query: (id) => ({ url: `/goals/${id}`, method: 'DELETE' }),
      // The API blocks deleting a goal with linked service lines, so a successful delete
      // never touches the service-lines list — only the goals list.
      invalidatesTags: [{ type: 'carePlanGoal', id: 'LIST' }],
    }),
    // Bulk reorder — persists the goals display order (sequence = index in `order`).
    reorderCarePlanGoals: builder.mutation<void, { planId: string; order: string[] }>({
      query: ({ planId, order }) => ({ url: `/${planId}/goals/sort-order`, method: 'PATCH', body: { order } }),
      invalidatesTags: [{ type: 'carePlanGoal', id: 'LIST' }],
    }),

    // ── Service lines (full CRUD) ───────────────────────────────────────────
    getCarePlanServiceLines: builder.query<CarePlanServiceLine[], CarePlanChildListReq>({
      query: ({ planId }) => ({ url: `/${planId}/service-lines`, method: 'GET' }),
      providesTags: [{ type: 'carePlanServiceLine', id: 'LIST' }],
      transformResponse: (payload) => unwrap<CarePlanServiceLine[]>(payload),
    }),
    createCarePlanServiceLine: builder.mutation<CarePlanServiceLine, { planId: string } & Partial<CarePlanServiceLine>>(
      {
        query: ({ planId, ...body }) => ({ url: `/${planId}/service-lines`, method: 'POST', body }),
        invalidatesTags: [{ type: 'carePlanServiceLine', id: 'LIST' }],
        transformResponse: (payload) => unwrap<CarePlanServiceLine>(payload),
      },
    ),
    updateCarePlanServiceLine: builder.mutation<void, { id: string } & Partial<CarePlanServiceLine>>({
      query: ({ id, ...body }) => ({ url: `/service-lines/${id}`, method: 'PATCH', body }),
      invalidatesTags: [{ type: 'carePlanServiceLine', id: 'LIST' }],
    }),
    deleteCarePlanServiceLine: builder.mutation<void, string>({
      query: (id) => ({ url: `/service-lines/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'carePlanServiceLine', id: 'LIST' }],
    }),

    // ── Cultural safety (1:1 per plan — no list, no delete) ─────────────────
    getCarePlanCulturalSafety: builder.query<CarePlanCulturalSafety | null, CarePlanChildListReq>({
      query: ({ planId }) => ({ url: `/${planId}/cultural-safety`, method: 'GET' }),
      providesTags: [{ type: 'carePlanCulturalSafety', id: 'SINGLETON' }],
      // No record → API omits `data`, so unwrap yields the envelope; coerce non-records to null.
      transformResponse: (payload) => {
        const data = unwrap<CarePlanCulturalSafety | null>(payload);
        return data && typeof data === 'object' && 'id' in data ? data : null;
      },
    }),
    createCarePlanCulturalSafety: builder.mutation<
      CarePlanCulturalSafety,
      { planId: string } & Partial<CarePlanCulturalSafety>
    >({
      query: ({ planId, ...body }) => ({ url: `/${planId}/cultural-safety`, method: 'POST', body }),
      invalidatesTags: [{ type: 'carePlanCulturalSafety', id: 'SINGLETON' }],
      transformResponse: (payload) => unwrap<CarePlanCulturalSafety>(payload),
    }),
    updateCarePlanCulturalSafety: builder.mutation<void, { id: string } & Partial<CarePlanCulturalSafety>>({
      query: ({ id, ...body }) => ({ url: `/cultural-safety/${id}`, method: 'PATCH', body }),
      invalidatesTags: [{ type: 'carePlanCulturalSafety', id: 'SINGLETON' }],
    }),

    // ── Reviews (append-only — creating a review cascades review_due_date to the plan) ──
    getCarePlanReviews: builder.query<CarePlanReview[], CarePlanChildListReq>({
      query: ({ planId }) => ({ url: `/${planId}/reviews`, method: 'GET' }),
      providesTags: [{ type: 'carePlanReview', id: 'LIST' }],
      transformResponse: (payload) => unwrap<CarePlanReview[]>(payload),
    }),
    // review_due_date is not a review column — the API cascades it onto the parent
    // plan's review_due_date, so it's accepted in the create body alongside the review.
    createCarePlanReview: builder.mutation<
      CarePlanReview,
      { planId: string; review_due_date?: string | null } & Partial<CarePlanReview>
    >({
      query: ({ planId, ...body }) => ({ url: `/${planId}/reviews`, method: 'POST', body }),
      // The parent plan's review_due_date + summary status change on the backend,
      // so invalidate the plan caches too.
      invalidatesTags: [
        { type: 'carePlanReview', id: 'LIST' },
        { type: 'carePlan', id: 'LIST' },
        { type: 'carePlan', id: 'SUMMARY' },
      ],
      transformResponse: (payload) => unwrap<CarePlanReview>(payload),
    }),

    // ── Notifications (create-only audit trail — get-one, get-all, create) ───
    getCarePlanNotifications: builder.query<CarePlanNotification[], CarePlanChildListReq>({
      query: ({ planId }) => ({ url: `/${planId}/notifications`, method: 'GET' }),
      providesTags: [{ type: 'carePlanNotification', id: 'LIST' }],
      transformResponse: (payload) => unwrap<CarePlanNotification[]>(payload),
    }),
    createCarePlanNotification: builder.mutation<
      CarePlanNotification,
      { planId: string } & Partial<CarePlanNotification>
    >({
      query: ({ planId, ...body }) => ({ url: `/${planId}/notifications`, method: 'POST', body }),
      invalidatesTags: [{ type: 'carePlanNotification', id: 'LIST' }],
      transformResponse: (payload) => unwrap<CarePlanNotification>(payload),
    }),
    getCarePlanNotification: builder.query<CarePlanNotification, string>({
      query: (id) => ({ url: `/notifications/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'carePlanNotification', id }],
      transformResponse: (payload) => unwrap<CarePlanNotification>(payload),
    }),

    // ── Lookups (read-only) ─────────────────────────────────────────────────
    getGoalStatuses: builder.query<CarePlanLookup[], void>({
      query: () => ({ url: '/lookups/goal-statuses', method: 'GET' }),
      transformResponse: (payload) => unwrap<CarePlanLookup[]>(payload),
    }),
    getGoalApproachTypes: builder.query<CarePlanLookup[], void>({
      query: () => ({ url: '/lookups/goal-approach-types', method: 'GET' }),
      transformResponse: (payload) => unwrap<CarePlanLookup[]>(payload),
    }),
    getReviewTypes: builder.query<CarePlanLookup[], void>({
      query: () => ({ url: '/lookups/review-types', method: 'GET' }),
      transformResponse: (payload) => unwrap<CarePlanLookup[]>(payload),
    }),
    getReviewTriggerTypes: builder.query<CarePlanLookup[], void>({
      query: () => ({ url: '/lookups/review-trigger-types', method: 'GET' }),
      transformResponse: (payload) => unwrap<CarePlanLookup[]>(payload),
    }),

    // External lookups for the service form: appointment service types + calendar
    // frequencies live off the care-plans base URL, so fetch them via queryFn (like
    // getCarePlans) to keep the tab on RTK Query. Active service types only.
    getAppointmentServiceTypes: builder.query<AppointmentServiceTypeOption[], void>({
      async queryFn(_arg, _api) {
        try {
          const payload = await get(
            apiAppointmentServiceTypes,
            { deleted: false, sort: ['name'], attributes: ['id', 'name'], limit: 0 },
            undefined,
            _api.signal,
          );
          return { data: unwrap<AppointmentServiceTypeOption[]>(payload) };
        } catch (e) {
          return { error: toApiError(e, 'getAppointmentServiceTypes') };
        }
      },
    }),
    getCalendarFrequencies: builder.query<CalendarFrequencyOption[], void>({
      async queryFn(_arg, _api) {
        try {
          const payload = await get(apiCalendarFrequencies, undefined, undefined, _api.signal);
          return { data: unwrap<CalendarFrequencyOption[]>(payload) };
        } catch (e) {
          return { error: toApiError(e, 'getCalendarFrequencies') };
        }
      },
    }),
  }),
});

export const {
  useGetCarePlansQuery,
  useLazyGetCarePlansQuery,
  useGetCarePlanQuery,
  useLazyGetCarePlanQuery,
  useGetClientsSummaryQuery,
  useLazyGetClientsSummaryQuery,
  useCreateCarePlanMutation,
  useUpdateCarePlanMutation,
  useActivateCarePlanMutation,
  useLinkCarePlanBudgetMutation,
  useUnlinkCarePlanBudgetMutation,
  useGetCarePlanNeedsQuery,
  useCreateCarePlanNeedMutation,
  useUpdateCarePlanNeedMutation,
  useDeleteCarePlanNeedMutation,
  useReorderCarePlanNeedsMutation,
  useGetCarePlanGoalsQuery,
  useCreateCarePlanGoalMutation,
  useUpdateCarePlanGoalMutation,
  useDeleteCarePlanGoalMutation,
  useReorderCarePlanGoalsMutation,
  useGetCarePlanServiceLinesQuery,
  useCreateCarePlanServiceLineMutation,
  useUpdateCarePlanServiceLineMutation,
  useDeleteCarePlanServiceLineMutation,
  useGetCarePlanCulturalSafetyQuery,
  useCreateCarePlanCulturalSafetyMutation,
  useUpdateCarePlanCulturalSafetyMutation,
  useGetCarePlanReviewsQuery,
  useCreateCarePlanReviewMutation,
  useGetCarePlanNotificationsQuery,
  useLazyGetCarePlanNotificationsQuery,
  useCreateCarePlanNotificationMutation,
  useGetCarePlanNotificationQuery,
  useLazyGetCarePlanNotificationQuery,
  useGetGoalStatusesQuery,
  useGetGoalApproachTypesQuery,
  useGetReviewTypesQuery,
  useGetReviewTriggerTypesQuery,
  useGetAppointmentServiceTypesQuery,
  useGetCalendarFrequenciesQuery,
} = carePlansApi;
